import { FunctionComponent, ReactNode } from 'react';
import { ErrorBoundary as ExternalErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Outlet } from 'react-router-dom';

import SentimentDissatisfiedOutlined from '@mui/icons-material/SentimentDissatisfiedOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ErrorPage } from '@climatepartner/components';

import { DomainError } from '../domain/error';
import { ObservableError } from '../domain/errors/observable.error';
import { useMonitor } from '../hooks/useMonitor';

const GeneralErrorMessage: FunctionComponent = () => {
    return (
        <Typography>
            Please contact{' '}
            <Typography
                component="a"
                variant="button"
                color="primary.main"
                href="https://teams.microsoft.com/l/channel/19%3AOGnmXC7hleVWQ4EvDUnZUO9g9IRSa1j3vBjkWzjpzLA1%40thread.tacv2/General?groupId=157fb20d-5f35-4807-b549-aef8d4b1b320&tenantId=72c79b81-6f12-454a-b310-b3c4d9c004c7"
            >
                Ecopilot team{' '}
            </Typography>
        </Typography>
    );
};

const ObservableErrorMessage: FunctionComponent = () => {
    return <Typography>Ecopilot team has been notified and is tacking your problem.</Typography>;
};

const Fallback: FunctionComponent<FallbackProps> = ({ error, resetErrorBoundary }) => {
    const renderMessage = () => {
        if (error instanceof ObservableError) {
            return <ObservableErrorMessage />;
        }
        return <GeneralErrorMessage />;
    };

    const renderAction = () => {
        if (error instanceof DomainError && error.recoverable) {
            return (
                <Box>
                    <Button onClick={resetErrorBoundary}>Continue</Button>
                </Box>
            );
        }
        return null;
    };

    return (
        <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center' }}>
            <ErrorPage title="" subtitle={'Something went wrong'} icon={<SentimentDissatisfiedOutlined />}>
                <Stack spacing={1}>
                    {renderMessage()}
                    {renderAction()}
                </Stack>
            </ErrorPage>
        </Box>
    );
};

const ErrorBoundary = ({ children = <Outlet /> }: { children?: ReactNode }) => {
    const { monitor } = useMonitor();

    return (
        <ExternalErrorBoundary
            FallbackComponent={Fallback}
            onError={(error) => {
                if (error instanceof DomainError) {
                    monitor.error(error);
                }
            }}
        >
            {children}
        </ExternalErrorBoundary>
    );
};

export default ErrorBoundary;
